import React, { useEffect, useState } from 'react';

import {
  Alert,
  Button,
  Col,
  Divider,
  Form,
  Input,
  Modal,
  Row,
  Select,
} from 'antd';
import PhoneInput from 'antd-phone-input';
import { useTranslation } from 'react-i18next';

import { ItemInterface } from 'models/ItemInterface';
import { Role } from 'models/Role';

import appI18n from '../../../../config/i18n';

interface NewRoleModalProps {
  isOpen: boolean;
  handleModalCancel: () => void;
  submit: (
    name: string,
    lastname: string,
    email: string,
    dialCode: string | null,
    phone: string | null,
    roleIds: number[]
  ) => void;
  roles: Role[];
  defaultCountryCode: string;
}

function NewUserModal({
  isOpen,
  handleModalCancel,
  submit,
  roles,
  defaultCountryCode,
}: NewRoleModalProps) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation(undefined, { i18n: appI18n });

  useEffect(() => {
    form.resetFields();

    if (isOpen) {
      form.setFieldValue('phone', {
        ...form.getFieldValue('phone'),
        countryCode: parseInt(defaultCountryCode),
      });
    }
  }, [isOpen]);

  const onFinish = async (values: any) => {
    setLoading(true);

    await submit(
      values.name,
      values.lastname,
      values.email,
      values.phone.phoneNumber === null ? null : `+${values.phone.countryCode}`,
      values.phone.phoneNumber === null
        ? null
        : `${values.phone.areaCode}${values.phone.phoneNumber}`,
      values.roleIds || []
    );

    setLoading(false);
  };

  const getRoles = (): ItemInterface[] => {
    return roles.map((record) => {
      return {
        value: record.id,
        label: record.name,
      };
    });
  };

  return (
    <>
      <Modal
        title={t('scenes.settings.users.components.new_user_modal.title')}
        open={isOpen}
        onCancel={handleModalCancel}
        maskClosable={false}
        destroyOnClose
        footer={null}
        width={480}
      >
        <Divider style={{ marginTop: '15px', marginBottom: '15px' }} />

        <Row gutter={24} style={{ marginBottom: '18px' }}>
          <Col xs={24}>
            <Alert
              message={t(
                'scenes.settings.users.components.new_user_modal.alert_info'
              )}
              type="info"
              showIcon
            />
          </Col>
        </Row>

        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Row gutter={24}>
            <Col xs={24} lg={24}>
              <Form.Item
                name="email"
                label={t(
                  'scenes.settings.users.components.new_user_modal.labels.email'
                )}
                rules={[
                  {
                    required: true,
                    message: t(
                      'scenes.settings.users.components.new_user_modal.validations.email.required'
                    ),
                  },
                  {
                    type: 'email',
                    message: t(
                      'scenes.settings.users.components.new_user_modal.validations.email.invalid'
                    ),
                  },
                ]}
              >
                <Input
                  type="text"
                  disabled={loading}
                  placeholder={t(
                    'scenes.settings.users.components.new_user_modal.placeholders.email'
                  )}
                  addonAfter={'@'}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col xs={24} lg={24}>
              <Form.Item
                name="name"
                label={t(
                  'scenes.settings.users.components.new_user_modal.labels.name'
                )}
                rules={[
                  {
                    required: true,
                    message: t(
                      'scenes.settings.users.components.new_user_modal.validations.name.required'
                    ),
                  },
                ]}
              >
                <Input
                  type="text"
                  disabled={loading}
                  placeholder={t(
                    'scenes.settings.users.components.new_user_modal.placeholders.name'
                  )}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col xs={24} lg={24}>
              <Form.Item
                name="lastname"
                label={t(
                  'scenes.settings.users.components.new_user_modal.labels.lastname'
                )}
                rules={[
                  {
                    required: true,
                    message: t(
                      'scenes.settings.users.components.new_user_modal.validations.lastname.required'
                    ),
                  },
                ]}
              >
                <Input
                  type="text"
                  disabled={loading}
                  placeholder={t(
                    'scenes.settings.users.components.new_user_modal.placeholders.lastname'
                  )}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col xs={24}>
              <Form.Item
                name="phone"
                label={t(
                  'scenes.settings.users.components.new_user_modal.labels.phone'
                )}
                rules={[
                  {
                    validator: (_, value) => {
                      if (!value) {
                        return Promise.resolve();
                      }

                      if (value.valid()) {
                        return Promise.resolve();
                      }

                      if (!value.valid() && value.phoneNumber === null) {
                        return Promise.resolve();
                      }

                      return Promise.reject(
                        new Error(
                          t(
                            'scenes.settings.users.components.new_user_modal.validations.phone.invalid'
                          )
                        )
                      );
                    },
                  },
                ]}
              >
                <PhoneInput
                  enableSearch
                  searchNotFound={t(
                    'scenes.settings.users.components.new_user_modal.phone.search_not_found'
                  )}
                  searchPlaceholder={t(
                    'scenes.settings.users.components.new_user_modal.phone.search_placeholder'
                  )}
                  allowClear
                  placeholder={t(
                    'scenes.settings.users.components.new_user_modal.placeholders.phone'
                  )}
                  disabled={loading}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col xs={24} lg={24}>
              <Form.Item
                name="roleIds"
                label={t(
                  'scenes.settings.users.components.new_user_modal.labels.roles'
                )}
              >
                <Select
                  mode="multiple"
                  style={{ width: '100%' }}
                  options={getRoles()}
                  disabled={loading}
                  placeholder={t(
                    'scenes.settings.users.components.new_user_modal.placeholders.roles'
                  )}
                  allowClear
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col xs={24}>
              <Button
                onClick={() => {
                  form.submit();
                }}
                loading={loading}
                type="primary"
                block
              >
                {t(
                  'scenes.settings.users.components.new_user_modal.buttons.save_changes'
                )}
              </Button>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
}

export default NewUserModal;
